import { Trans, useLingui } from "@lingui/react/macro";
import type { PixCharge } from "@orbin/contracts";
import { Button } from "@orbin/ui-web";
import { Check, Copy } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatBrl, needsPayerIdentification, PIX_AMOUNTS_CENTS } from "../lib/metering";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";
import { PaymentAmountPicker } from "./PaymentAmountPicker";
import { TaxIdField } from "./TaxIdField";

const POLL_MS = 3000;

function remaining(expiresAt: string, now: number): string {
  const seconds = Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

/** Picks an amount, shows the PIX code and QR, and turns to paid when the charge settles. */
export function PixCheckout({
  onPaid,
  amounts = PIX_AMOUNTS_CENTS,
}: {
  onPaid?: (charge: PixCharge) => void;
  amounts?: readonly number[];
}) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale || "en";
  const [amount, setAmount] = useState<number>(amounts[1] ?? amounts[0] ?? 0);
  const [charge, setCharge] = useState<PixCharge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsTaxId, setNeedsTaxId] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const attempt = useRef(0);
  const nonce = useRef(crypto.randomUUID());
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  const create = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (charge) attempt.current += 1;
      const next = await rpc.payments.createPixCharge({
        amountCents: amount,
        clientNonce: `${nonce.current}-${amount}-${attempt.current}`,
      });
      setCharge(next);
    } catch (cause) {
      if (needsPayerIdentification(cause)) setNeedsTaxId(true);
      else setError(errorText(cause, t`Could not create the PIX code`));
    } finally {
      setBusy(false);
    }
  }, [amount, busy, t]);

  const pending = charge?.status === "pending";
  const chargeId = charge?.id;
  useEffect(() => {
    if (!chargeId || !pending) return;
    let active = true;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      void rpc.payments
        .chargeStatus({ chargeId })
        .then((next) => {
          if (!active) return;
          setCharge(next);
          if (next.status === "paid") onPaidRef.current?.(next);
        })
        .catch(() => undefined);
    }, POLL_MS);
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.clearInterval(tick);
    };
  }, [chargeId, pending]);

  async function copy() {
    if (!charge) return;
    try {
      await navigator.clipboard.writeText(charge.qrCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t`Could not copy the code`);
    }
  }

  const expired =
    charge !== null &&
    (charge.status === "expired" || (pending && Date.parse(charge.expiresAt) <= now));

  if (charge?.status === "paid") {
    return (
      <div
        role="status"
        data-testid="pix-paid"
        className="flex flex-col items-center gap-3 py-6 text-center"
      >
        <span className="flex size-10 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="size-5" aria-hidden="true" />
        </span>
        <p className="text-[15px] font-medium text-foreground">
          <Trans>Payment received</Trans>
        </p>
        <p className="text-sm text-muted-foreground">{formatBrl(charge.amountCents, locale)}</p>
      </div>
    );
  }

  if (charge && !expired && charge.status === "pending") {
    return (
      <div data-testid="pix-code" className="flex flex-col items-center gap-4">
        {charge.qrCodeBase64 ? (
          <img
            src={
              charge.qrCodeBase64.startsWith("data:")
                ? charge.qrCodeBase64
                : `data:image/png;base64,${charge.qrCodeBase64}`
            }
            alt={t`PIX QR code`}
            className="size-48 rounded-lg bg-white p-2"
          />
        ) : null}
        <p className="text-sm tabular-nums text-muted-foreground">
          {formatBrl(charge.amountCents, locale)} · {remaining(charge.expiresAt, now)}
        </p>
        <Button variant="secondary" className="w-full" onClick={() => void copy()}>
          {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
          {copied ? t`Copied` : t`Copy PIX code`}
        </Button>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {needsTaxId ? (
        <TaxIdField
          onSaved={() => {
            setNeedsTaxId(false);
            void create();
          }}
        />
      ) : null}
      {expired ? (
        <p role="status" className="text-sm text-muted-foreground">
          <Trans>This code expired.</Trans>
        </p>
      ) : null}
      <PaymentAmountPicker
        amounts={amounts}
        value={amount}
        onChange={(cents) => {
          attempt.current += 1;
          setAmount(cents);
        }}
      />
      <Button disabled={busy} onClick={() => void create()}>
        <Trans>Pay with PIX</Trans>
      </Button>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
